import React, { useState, useCallback } from 'react';
import { ChevronRight, ChevronLeft, User, FileText, Mic, CheckCircle2, Globe, MessageSquare, Shield, Briefcase, ClipboardList, Zap, Accessibility, LifeBuoy, Sparkles, Inbox, Send, CalendarDays, ArrowRightCircle, Medal, ListChecks, Gavel, UserCheck, MoreHorizontal, Pill, Thermometer, Star } from 'lucide-react';
import { Button } from '../ui/button';
import { Card, CardContent } from '../ui/card';
import { PATIENT_STATUSES, LETTER_TYPES, MESSAGE_SCENARIOS } from '../../constants/mediscribe';
import NSISymptomTracker from './NSISymptomTracker';
import { MATERIAL_STYLES } from './RoleSelector';

// Patient status groups for tile filtering.
// `MILITARY` = anyone on active duty / activated guard / reserves.
// `EMPLOYEE` = civilian/veteran/retiree/dependent (i.e. anyone who is
//              employed by a private/non-DoD employer; FMLA, ADA, RTW,
//              private LTD all apply here, NOT to active-duty soldiers).
// `ALL`      = every persona.
const MILITARY_ROLES = ['active_duty', 'active_guard', 'reserve'];
const EMPLOYEE_ROLES = ['civilian', 'veteran', 'retiree', 'dependent'];
const ALL_ROLES      = [...MILITARY_ROLES, ...EMPLOYEE_ROLES];

// Feb 28, 2026 (v6.8.42) — Sectioned + gem-styled. The PatientWizard Step 1
// QUICK_LETTERS now mirror the exact look of the RoleSelector "Quick Start"
// gem tiles (copper-trimmed sections, material gradients, Cinzel labels).
// `roles` controls persona filtering; `material` controls visual treatment.
const QUICK_LETTER_SECTIONS = [
  {
    title: 'Before your appointment',
    items: [
      { id: 'visit_prep', label: 'Visit Prep Script', desc: 'Talking points for visit', icon: Sparkles, material: 'navy_gem', documentMode: 'script', letterType: 'visit_prep_script', roles: ALL_ROLES },
      { id: 'other', label: 'Other', desc: 'Custom letter or request', icon: MoreHorizontal, material: 'slate_stone', documentMode: 'letter', letterType: 'other', roles: ALL_ROLES },
    ],
  },
  {
    title: 'Messages to your provider',
    items: [
      { id: 'portal_duty', label: 'Portal Msg: Duty', desc: 'Request profile/restrictions', icon: Shield, material: 'orange_agate', documentMode: 'message', letterType: '', messageScenario: 'duty_limitation_letter', roles: MILITARY_ROLES },
      { id: 'portal_profile', label: 'Portal Msg: Profile', desc: 'Renewal or question', icon: ClipboardList, material: 'navy_gem', documentMode: 'message', letterType: '', messageScenario: 'profile_restriction_question', roles: MILITARY_ROLES },
      { id: 'portal_meds', label: 'Portal Msg: Meds', desc: 'Refill or question', icon: Pill, material: 'emerald_gem', documentMode: 'message', letterType: '', messageScenario: 'refill_same_pharmacy', roles: ALL_ROLES },
      { id: 'portal_results', label: 'Portal Msg: Results', desc: 'Lab or test question', icon: MessageSquare, material: 'teal_slate', documentMode: 'message', letterType: '', messageScenario: 'lab_results', roles: ALL_ROLES },
      { id: 'portal_symptom', label: 'Portal Msg: Symptom', desc: 'New or worsening', icon: Thermometer, material: 'burgundy_leather', documentMode: 'message', letterType: '', messageScenario: 'urgent_concern', roles: ALL_ROLES },
      { id: 'portal_general', label: 'Portal Msg: General', desc: 'Any message request', icon: Inbox, material: 'deep_purple', documentMode: 'message', letterType: '', roles: ALL_ROLES },
    ],
  },
  {
    title: 'Letters from your provider',
    items: [
      { id: 'referral', label: 'Referral', desc: 'Request or issue', icon: Send, material: 'teal_slate', documentMode: 'letter', letterType: 'referral_request', roles: ALL_ROLES },
      { id: 'fmla', label: 'FMLA', desc: 'Medical Leave', icon: CalendarDays, material: 'brushed_gold', documentMode: 'letter', letterType: 'fmla_intermittent', roles: EMPLOYEE_ROLES },
      { id: 'ada', label: 'ADA', desc: 'Workplace', icon: Accessibility, material: 'dark_slate', documentMode: 'letter', letterType: 'ada_accommodations', roles: EMPLOYEE_ROLES },
      { id: 'rtw', label: 'RTW', desc: 'Return to Work', icon: ArrowRightCircle, material: 'copper', documentMode: 'letter', letterType: 'return_to_work', roles: EMPLOYEE_ROLES },
      { id: 'command', label: 'Command', desc: 'Commander/1SG', icon: Medal, material: 'bronze', documentMode: 'letter', letterType: 'letter_to_command', roles: MILITARY_ROLES },
      { id: 'profile', label: 'Duty Limits', desc: 'Restrictions', icon: ListChecks, material: 'steel_blue', documentMode: 'letter', letterType: 'custom', isLimitations: true, roles: MILITARY_ROLES },
    ],
  },
  {
    title: 'Appeals & advocacy',
    items: [
      { id: 'ides_appeal', label: 'IDES Appeal', desc: '1st person rebuttal', icon: Gavel, material: 'obsidian', documentMode: 'letter', letterType: 'ides_appeal_soldier', roles: MILITARY_ROLES },
      { id: 'buddy_statement', label: 'Buddy Statement', desc: 'Character witness for IDES', icon: UserCheck, material: 'rose_marble', documentMode: 'letter', letterType: 'buddy_statement', roles: MILITARY_ROLES },
      // Mar 3, 2026 — VA claim appeal is DISTINCT from IDES. Available to
      // veterans, retirees, dependents, AND active duty (soldiers file
      // pre-separation with intent to file). NOD/HLR/Supplemental/BVA.
      { id: 'va_claim_appeal', label: 'VA Claim Appeal', desc: 'NOD / HLR / Supplemental / BVA', icon: Gavel, material: 'burgundy_leather', documentMode: 'letter', letterType: 'va_claim_appeal', roles: ALL_ROLES },
      { id: 'va_nexus_request', label: 'Ask for Nexus Letter', desc: 'Request provider VA nexus letter', icon: Send, material: 'deep_purple', documentMode: 'letter', letterType: 'va_nexus_letter_request', roles: ALL_ROLES },
      // SSA / LTD long-term disability helpers (Feb 28, 2026 — v6.8.39).
      { id: 'ssa_disability', label: 'SSA Disability', desc: 'Bluebook functional statement (1st person)', icon: Accessibility, material: 'navy_gem', documentMode: 'letter', letterType: 'ssa_disability_statement', roles: ALL_ROLES },
      { id: 'ltd_application', label: 'LTD Application', desc: 'Long-Term Disability statement (1st person)', icon: LifeBuoy, material: 'brushed_gold', documentMode: 'letter', letterType: 'ltd_application_statement', roles: EMPLOYEE_ROLES },
    ],
  },
];

// Flat list retained for legacy code paths (extraction, search, role filter).
const QUICK_LETTERS = QUICK_LETTER_SECTIONS.flatMap(s => s.items);

/**
 * PatientWizard — Step-by-step guided flow for patients needing admin letters.
 * Supports bilingual input (English/Spanish) with automatic dual-language output.
 */
const STEPS = [
  { id: 'letter', label: 'Document Type', icon: FileText, description: 'Letter, portal message, or both' },
  { id: 'situation', label: 'Situation', icon: Mic, description: 'Describe what\'s going on' },
  { id: 'info', label: 'Your Info', icon: User, description: 'Name, rank, and unit' },
  { id: 'review', label: 'Review', icon: CheckCircle2, description: 'Review & generate' },
];

const PatientWizard = ({
  // Step 1: Demographics
  DemographicsComponent,
  demographicsProps,
  // Step 2: Notes
  NotesComponent,
  notesProps,
  LimitationsComponent,
  limitationsProps,
  // Step 3: Letter options
  LetterOptionsComponent,
  letterOptionsProps,
  // Step 4: Review & Generate
  ActionButtonsComponent,
  actionButtonsProps,
  // State
  notes,
  soldierFirstName,
  soldierLastName,
  patientStatus,
  letterType,
  effectiveRole,
  // Document mode
  documentMode,
  messageScenario,
  // Language
  language,
  setLanguage,
  // Recording
  RecordingComponent,
  recordingProps,
  // Quick Letter
  initialStep,
  // Feb 28, 2026 (v6.8.42) — Change persona shortcut.
  // Parent (App.js / PatientPortalView) provides this callback so the
  // user can jump back to "What best describes you?" directly from
  // Step 1 without losing wizard state mid-flow.
  onChangePersona,
}) => {
  const [currentStep, setCurrentStep] = useState(initialStep || 0);
  // Feb 28, 2026 (v6.8.41) — Show-all escape for the persona tile filter.
  // Persists OFF on mount (we want filtered view by default) but the user
  // can click "Show all" to see the hidden tiles for the current encounter.
  const [showAllQuickLetters, setShowAllQuickLetters] = useState(false);

  // Feb 28, 2026 (v6.8.47) — Persona → material gradient map.
  // The Change-Persona pill in Step 1 now mirrors the gem-card color the
  // user picked on "What best describes you?". Active Duty gets copper
  // (user request), Civilian gets dark_slate (matches the civilian role
  // card), Dependent gets navy_gem, VA Claims gets emerald_gem, etc.
  // Falls back to brushed_gold for any unrecognized status.
  const PERSONA_MATERIAL_MAP = {
    active_duty: 'copper',
    active_guard: 'copper',
    reserve: 'copper',
    veteran: 'steel_blue',
    retiree: 'bronze',
    dependent: 'navy_gem',
    civilian: 'dark_slate',
    va_claims: 'emerald_gem',
  };
  // Resolve effective patientStatus — fall back to patientType when
  // patientStatus is empty (va_claims path sets only patientType).
  const personaKey = patientStatus || (typeof window !== 'undefined' ? localStorage.getItem('writeright_patient_type') : null) || '';
  const personaMaterial = PERSONA_MATERIAL_MAP[personaKey] || 'brushed_gold';
  const personaMaterialClasses = MATERIAL_STYLES[personaMaterial] || MATERIAL_STYLES.brushed_gold;

  const wizardRef = React.useRef(null);

  const scrollToTop = useCallback(() => {
    // Mar 1, 2026 — On mobile, `scrollIntoView({behavior:'smooth'})` was
    // intermittently failing because the page's outer scroll container is
    // the <html> element AND `behavior:'smooth'` gets interrupted by
    // React's next-render-cycle scroll-anchoring. Hammer multiple targets
    // with instant scrolls so the viewport WILL pin to (0,0) regardless of
    // device, layout, or inner-scrollable ancestry.
    try { window.scrollTo({ top: 0, left: 0, behavior: 'auto' }); } catch { /* noop */ }
    try { document.documentElement.scrollTop = 0; } catch { /* noop */ }
    try { document.body.scrollTop = 0; } catch { /* noop */ }
    // Walk up the wizard ref's scrollable ancestors and zero each one.
    if (wizardRef.current) {
      let parent = wizardRef.current.parentElement;
      while (parent && parent !== document.body) {
        if (parent.scrollHeight > parent.clientHeight) parent.scrollTop = 0;
        parent = parent.parentElement;
      }
    }
    // One more pin after React's paint, to defeat scroll-anchor restoration.
    requestAnimationFrame(() => {
      try {
        window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
        document.documentElement.scrollTop = 0;
        document.body.scrollTop = 0;
      } catch { /* noop */ }
    });
  }, []);

  const nextStep = () => {
    setCurrentStep(prev => {
      const next = prev < STEPS.length - 1 ? prev + 1 : prev;
      // Auto-extract demographics from notes when moving from Situation (1) → Info (2)
      if (prev === 1 && next === 2 && notes) {
        const dp = demographicsProps;
        // Extract rank + name patterns: "SGT Smith", "PFC John Doe", "SPC Jane Doe"
        const rankPattern = /\b(PVT|PV2|PFC|SPC|CPL|SGT|SSG|SFC|MSG|1SG|SGM|CSM|2LT|1LT|CPT|MAJ|LTC|COL|BG|MG|LTG|GEN)\b[.\s]+([A-Z][a-z]+)(?:\s+([A-Z][a-z]+))?/i;
        const rankMatch = notes.match(rankPattern);
        if (rankMatch) {
          if (dp.setSoldierRank && !dp.soldierRank) dp.setSoldierRank(rankMatch[1].toUpperCase());
          if (rankMatch[3]) {
            if (dp.setSoldierFirstName && !dp.soldierFirstName) dp.setSoldierFirstName(rankMatch[2]);
            if (dp.setSoldierLastName && !dp.soldierLastName) dp.setSoldierLastName(rankMatch[3]);
          } else {
            if (dp.setSoldierLastName && !dp.soldierLastName) dp.setSoldierLastName(rankMatch[2]);
          }
        }
        // Extract DOB: "DOB 15 May 1990", "DOB: 05/15/1990", "date of birth 1990-05-15"
        const dobPatterns = [
          /(?:DOB|date of birth|born)[:\s]+(\d{1,2})[\/\-](\d{1,2})[\/\-](\d{2,4})/i,
          /(?:DOB|date of birth|born)[:\s]+(\d{1,2})\s+(Jan|Feb|Mar|Apr|May|Jun|Jul|Aug|Sep|Oct|Nov|Dec)\w*\s+(\d{4})/i,
          /(?:DOB|date of birth|born)[:\s]+(\d{4})[\/\-](\d{1,2})[\/\-](\d{1,2})/i,
        ];
        for (const pat of dobPatterns) {
          const m = notes.match(pat);
          if (m && dp.setSoldierDob && !dp.soldierDob) {
            // Try to build a date string the input can use
            try {
              const d = new Date(m[0].replace(/(?:DOB|date of birth|born)[:\s]+/i, ''));
              if (!isNaN(d.getTime())) {
                dp.setSoldierDob(d.toISOString().split('T')[0]);
              }
            } catch { /* skip */ }
            break;
          }
        }
        // Extract unit: "unit: 1-325 AIR", "assigned to 82nd ABN"
        const unitMatch = notes.match(/(?:unit|assigned to|stationed at)[:\s]+([^\n,]{3,30})/i);
        if (unitMatch && dp.setSoldierUnit && !dp.soldierUnit) {
          dp.setSoldierUnit(unitMatch[1].trim());
        }
      }
      if (next !== prev) setTimeout(scrollToTop, 50);
      return next;
    });
  };
  const prevStep = () => {
    setCurrentStep(prev => {
      const next = prev > 0 ? prev - 1 : prev;
      if (next !== prev) setTimeout(scrollToTop, 50);
      return next;
    });
  };
  const goToStep = (idx) => {
    setCurrentStep(idx);
    setTimeout(scrollToTop, 50);
  };

  const canProceed = useCallback(() => true, []);

  return (
    <div className="space-y-4" data-testid="patient-wizard" ref={wizardRef}>
      {/* Progress Steps */}
      <div className="flex items-center justify-between px-1" data-testid="wizard-progress">
        {STEPS.map((step, idx) => {
          const Icon = step.icon;
          const isActive = idx === currentStep;
          const isComplete = idx < currentStep;
          const isClickable = idx <= currentStep || canProceed();
          return (
            <React.Fragment key={step.id}>
              <button
                onClick={() => goToStep(idx)}
                disabled={!isClickable}
                className={`flex flex-col items-center gap-1 transition-all duration-200 group ${
                  isClickable ? 'cursor-pointer' : 'cursor-not-allowed opacity-40'
                }`}
                data-testid={`wizard-step-${step.id}`}
              >
                <div className={`w-10 h-10 rounded-full flex items-center justify-center transition-all duration-300 ${
                  isActive
                    ? 'bg-primary text-primary-foreground scale-110 shadow-lg shadow-primary/30'
                    : isComplete
                      ? 'bg-primary/20 text-primary border-2 border-primary/40'
                      : 'bg-muted/50 text-muted-foreground border border-border/50'
                }`}>
                  {isComplete ? <CheckCircle2 className="w-5 h-5" /> : <Icon className="w-5 h-5" />}
                </div>
                <span className={`text-[10px] sm:text-xs font-medium transition-colors ${
                  isActive ? 'text-primary' : isComplete ? 'text-foreground' : 'text-muted-foreground'
                }`}>
                  {step.label}
                </span>
              </button>
              {idx < STEPS.length - 1 && (
                <div className={`flex-1 h-0.5 mx-1 sm:mx-2 rounded-full transition-colors duration-300 ${
                  idx < currentStep ? 'bg-primary/60' : 'bg-border/50'
                }`} />
              )}
            </React.Fragment>
          );
        })}
      </div>

      {/* Progress Bar */}
      <div className="wizard-progress-bar" style={{ '--progress': `${((currentStep + 1) / STEPS.length) * 100}%` }} />

      {/* Step Content
          Feb 28, 2026 (v6.8.49) — Gem/copper facelift. Wraps the
          existing Card in a copper-trimmed dossier shell so the whole
          wizard surface matches the role-selector / Quick Start gem
          aesthetic. The inner Card keeps its existing layout so
          downstream children don't need refactoring. */}
      <div
        className="copper-shine-frame"
        style={{
          padding: '3px',
          boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 60px rgba(139, 69, 19, 0.18)',
        }}
        data-testid="wizard-content-shell"
      >
        <div
          style={{
            background: 'linear-gradient(180deg, hsl(220, 45%, 10%) 0%, hsl(220, 45%, 6%) 100%)',
            boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(218,165,32,0.12)',
          }}
        >
          <Card className="border-0 bg-transparent shadow-none" data-testid="wizard-content">
            <CardContent className="p-4 sm:p-6">
              {/* Step Header — Cinzel typography + copper underline. */}
              <div
                className="mb-5 pb-4"
                key={currentStep}
                style={{ borderBottom: '1px solid rgba(218,165,32,0.18)' }}
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <div
                        className="w-7 h-7 flex items-center justify-center"
                        style={{
                          background: 'linear-gradient(180deg, rgba(218,165,32,0.18), rgba(184,134,11,0.18))',
                          border: '1px solid rgba(218,165,32,0.35)',
                          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18), inset 0 -1px 0 rgba(0,0,0,0.35)',
                        }}
                      >
                        {React.createElement(STEPS[currentStep].icon, { className: 'w-4 h-4', style: { color: 'rgba(255, 244, 214, 0.92)' } })}
                      </div>
                      <span
                        className="text-[10px] tracking-widest uppercase"
                        style={{
                          fontFamily: '"Cinzel", serif',
                          fontWeight: 700,
                          letterSpacing: '0.20em',
                          color: 'rgba(218,165,32,0.92)',
                        }}
                      >
                        Step {currentStep + 1} / {STEPS.length}
                      </span>
                    </div>
                    <h3
                      className="leading-tight"
                      style={{
                        fontFamily: '"Cinzel", serif',
                        fontWeight: 700,
                        fontSize: '1.05rem',
                        letterSpacing: '0.05em',
                        textTransform: 'uppercase',
                        color: 'rgba(255, 248, 224, 0.96)',
                        textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                      }}
                    >
                      {STEPS[currentStep].label}
                    </h3>
                    <p
                      className="text-xs italic mt-1.5"
                      style={{
                        fontFamily: '"Libre Baskerville", serif',
                        color: 'rgba(255, 248, 224, 0.65)',
                      }}
                    >
                      {STEPS[currentStep].description}
                    </p>
                  </div>

              {/* Persona pill — only when a persona is locked + parent
                  provided the change-persona callback. v6.8.47 — pill
                  color now mirrors the role-card material the user
                  picked on "What best describes you?" (copper for Active
                  Duty, dark_slate for Civilian, navy_gem for Dependent,
                  emerald_gem for VA Claims, etc.) so the wizard chrome
                  matches the persona selection at every glance. */}
              {(patientStatus || personaKey) && onChangePersona && (
                <button
                  type="button"
                  onClick={onChangePersona}
                  data-testid="change-persona-link"
                  data-persona={personaKey}
                  data-persona-material={personaMaterial}
                  title="Change what best describes you"
                  className={`group relative flex items-center gap-2 px-3.5 py-1.5 shrink-0 border
                             ${personaMaterialClasses}
                             hover:-translate-y-0.5 active:translate-y-0 active:scale-[0.97]
                             transition-all duration-200`}
                  style={{
                    borderRadius: 0,
                    boxShadow: [
                      'inset 0 2px 0 rgba(255,255,255,0.45)',
                      'inset 0 -3px 0 rgba(0,0,0,0.45)',
                      'inset 2px 0 0 rgba(255,255,255,0.15)',
                      'inset -2px 0 0 rgba(0,0,0,0.3)',
                      '0 3px 8px -1px rgba(0,0,0,0.5)',
                    ].join(', '),
                  }}
                >
                  <span
                    className="pointer-events-none absolute inset-[3px]"
                    style={{ border: '1px solid rgba(255, 215, 130, 0.28)' }}
                  />
                  <User className="w-3 h-3 relative z-10" style={{ filter: 'drop-shadow(0 -1px 0 rgba(0,0,0,0.6)) drop-shadow(0 1px 0 rgba(255,255,255,0.4))' }} />
                  <span
                    className="text-[10px] tracking-widest font-bold relative z-10"
                    style={{
                      fontFamily: '"Cinzel", serif',
                      letterSpacing: '0.16em',
                      textTransform: 'uppercase',
                      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                    }}
                  >
                    {(PATIENT_STATUSES.find(p => p.value === personaKey)?.label || personaKey || patientStatus).replace(/\s*\(.*\)/, '')}
                  </span>
                  <span className="opacity-75 relative z-10">·</span>
                  <span
                    className="text-[10px] italic relative z-10 opacity-90"
                    style={{ fontFamily: '"Libre Baskerville", serif' }}
                  >change</span>
                </button>
              )}
            </div>
          </div>

          {/* Step 1: Document Type & Options */}
          {currentStep === 0 && (
            <div className="space-y-4 wizard-step-enter" data-testid="wizard-step-letter-content">
              <div className="p-3 bg-primary/5 border border-primary/10 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Choose <strong>Letter</strong> for a formal document, <strong>Portal Message</strong> for a quick inbox message, or <strong>Both</strong> to get both at once.
                </p>
              </div>

              {/* Manual dropdown FIRST (Feb 28, 2026 v6.8.43) — per user
                  feedback: experienced users want the manual list at the
                  top so they don't have to scroll past the Quick Start
                  gem tiles every encounter. Quick Start tiles still live
                  below as a one-tap shortcut for common scenarios. */}
              {LetterOptionsComponent && <LetterOptionsComponent {...letterOptionsProps} />}

              {/* Quick Letters — tap to pre-fill type.
                  Feb 28, 2026 (v6.8.42) — Gem-styled tiles now match the
                  RoleSelector "Quick Start" copper-trimmed dossier look. */}
              {!letterType && documentMode !== 'message' && (() => {
                const hasPersona = !!patientStatus;
                const allTiles = QUICK_LETTERS;
                // Mar 3, 2026 — Dedupe with the RoleSelector express row.
                // Before persona pick, the persona-picker still shows a
                // "Need help with disability claims?" express row with SSA
                // / LTD / IDES. Rendering them AGAIN in the "Appeals &
                // advocacy" section below is cognitive load without gain.
                // Hide the duplicates until persona is picked.
                const EXPRESS_ROW_IDS = new Set(['ides_appeal', 'ssa_disability', 'ltd_application']);
                const filteredTiles = !hasPersona
                  ? allTiles.filter(ql => !EXPRESS_ROW_IDS.has(ql.id))
                  : allTiles.filter(ql => !ql.roles || ql.roles.includes(patientStatus));
                const hiddenCount = allTiles.length - filteredTiles.length;
                const visibleIds = new Set((showAllQuickLetters ? allTiles : filteredTiles).map(t => t.id));
                // Filter each section's items and drop sections that ended
                // up empty so we don't render orphan headers.
                const sectionsToRender = QUICK_LETTER_SECTIONS
                  .map(s => ({ ...s, items: s.items.filter(it => visibleIds.has(it.id)) }))
                  .filter(s => s.items.length > 0);
                return (
                <div
                  className="copper-shine-frame"
                  style={{
                    padding: '3px',
                    boxShadow: '0 8px 32px rgba(0,0,0,0.5), 0 0 60px rgba(139, 69, 19, 0.18)',
                  }}
                  data-testid="quick-letters-section"
                >
                  <div
                    className="p-4 sm:p-5 space-y-5"
                    style={{
                      background: 'linear-gradient(180deg, hsl(220, 45%, 10%) 0%, hsl(220, 45%, 6%) 100%)',
                      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.6), inset 0 0 0 1px rgba(218,165,32,0.12)',
                    }}
                  >
                    {/* Section heading row — Quick Start label + (optional) show-all toggle */}
                    <div className="flex items-center gap-3">
                      <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(218,165,32,0.4), transparent)' }} />
                      <span
                        className="flex items-center gap-2 text-[11px] px-3 py-1 tracking-widest font-bold uppercase"
                        style={{
                          fontFamily: '"Cinzel", serif',
                          letterSpacing: '0.18em',
                          background: 'linear-gradient(180deg, #ffd700 0%, #daa520 50%, #b8860b 100%)',
                          WebkitBackgroundClip: 'text',
                          WebkitTextFillColor: 'transparent',
                          backgroundClip: 'text',
                        }}
                      >
                        <Zap className="w-3 h-3 text-amber-300" /> Quick Start
                      </span>
                      <div className="h-px flex-1" style={{ background: 'linear-gradient(90deg, transparent, rgba(218,165,32,0.4), transparent)' }} />
                    </div>
                    <p
                      className="text-center text-[11px] text-amber-100/60 italic"
                      style={{ fontFamily: '"Libre Baskerville", serif' }}
                    >
                      Tap a tile to pre-fill — or choose manually below.
                    </p>

                    {hasPersona && hiddenCount > 0 && (
                      <div className="flex justify-end">
                        <button
                          type="button"
                          onClick={() => setShowAllQuickLetters(s => !s)}
                          className="text-[10px] px-2.5 py-1 rounded-full border border-amber-300/30 text-amber-100/75 hover:text-amber-50 hover:border-amber-300/60 transition-colors"
                          data-testid="quick-letters-show-all-toggle"
                          style={{ fontFamily: '"Libre Baskerville", serif' }}
                        >
                          {showAllQuickLetters
                            ? `Hide ${hiddenCount} non-matching`
                            : `Show all ${allTiles.length} (+${hiddenCount} hidden for your persona)`}
                        </button>
                      </div>
                    )}

                    {/* Sections — v6.8.49 more breathing room */}
                    <div className="space-y-7 mt-1">
                      {sectionsToRender.map(section => (
                        <div key={section.title} className="pt-2">
                          <h4
                            className="text-[12px] mb-4 text-center sm:text-left"
                            style={{
                              fontFamily: '"Cinzel", serif',
                              fontWeight: 700,
                              letterSpacing: '0.20em',
                              textTransform: 'uppercase',
                              color: 'rgba(218,165,32,0.92)',
                              paddingBottom: '8px',
                              borderBottom: '1px solid rgba(218,165,32,0.25)',
                            }}
                          >
                            {section.title}
                          </h4>
                          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                            {section.items.map(ql => {
                              const Icon = ql.icon;
                              const materialClasses = MATERIAL_STYLES[ql.material] || MATERIAL_STYLES.slate_stone;
                              return (
                                <button
                                  key={ql.id}
                                  type="button"
                                  onClick={() => {
                                    if (ql.documentMode === 'message') {
                                      letterOptionsProps.setDocumentMode('message');
                                      if (ql.messageScenario && letterOptionsProps.setMessageScenario) {
                                        letterOptionsProps.setMessageScenario(ql.messageScenario);
                                      }
                                    } else {
                                      letterOptionsProps.setDocumentMode('letter');
                                      letterOptionsProps.setLetterType(ql.letterType);
                                      letterOptionsProps.setGenerateAdminLetter(true);
                                    }
                                    if (ql.isLimitations && limitationsProps?.setGenerateLimitations) {
                                      limitationsProps.setGenerateLimitations(true);
                                    }
                                    nextStep();
                                  }}
                                  data-testid={`quick-letter-${ql.id}`}
                                  style={{
                                    borderRadius: 0,
                                    boxShadow: [
                                      'inset 0 3px 0 rgba(255,255,255,0.55)',
                                      'inset 0 -4px 0 rgba(0,0,0,0.5)',
                                      'inset 3px 0 0 rgba(255,255,255,0.18)',
                                      'inset -3px 0 0 rgba(0,0,0,0.35)',
                                      '0 5px 10px -2px rgba(0,0,0,0.55)',
                                    ].join(', '),
                                  }}
                                  className={`group relative h-[110px] flex flex-col items-center justify-center gap-1 p-2 border rounded-none ${materialClasses}
                                              hover:-translate-y-1 active:translate-y-0 active:scale-[0.97] transition-all duration-200`}
                                >
                                  <div
                                    className="pointer-events-none absolute inset-[4px]"
                                    style={{ border: '1px solid rgba(255, 215, 130, 0.22)' }}
                                  />
                                  <Icon
                                    className="w-5 h-5 mb-0.5 relative z-10"
                                    strokeWidth={2}
                                    style={{
                                      color: 'rgba(255, 244, 214, 0.92)',
                                      filter: [
                                        'drop-shadow(0 -1px 0 rgba(0,0,0,0.85))',
                                        'drop-shadow(0 1px 0 rgba(255,255,255,0.5))',
                                        'drop-shadow(0 -1px 2px rgba(0,0,0,0.4))',
                                      ].join(' '),
                                    }}
                                  />
                                  <span
                                    className="text-[10px] leading-tight text-center px-1 relative z-10"
                                    style={{
                                      fontFamily: '"Cinzel", serif',
                                      fontWeight: 700,
                                      letterSpacing: '0.04em',
                                      textTransform: 'uppercase',
                                      color: 'rgba(255, 248, 224, 0.96)',
                                      textShadow: '0 1px 2px rgba(0,0,0,0.5)',
                                    }}
                                  >{ql.label}</span>
                                  <span
                                    className="text-[9px] leading-tight text-center px-1 relative z-10 italic"
                                    style={{
                                      fontFamily: '"Libre Baskerville", serif',
                                      color: 'rgba(255, 248, 224, 0.65)',
                                    }}
                                  >{ql.desc}</span>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="pt-1">
                      <div className="h-px" style={{ background: 'linear-gradient(90deg, transparent, rgba(218,165,32,0.25), transparent)' }} />
                      <p
                        className="text-[10px] text-amber-100/55 text-center pt-2 italic"
                        style={{ fontFamily: '"Libre Baskerville", serif' }}
                      >
                        Or choose manually below
                      </p>
                    </div>
                  </div>
                </div>
                );
              })()}

              {/* LetterOptionsComponent moved ABOVE Quick Start in v6.8.43 */}
            </div>
          )}

          {/* Step 2: Describe Your Situation */}
          {currentStep === 1 && (
            <div className="space-y-4 wizard-step-enter" data-testid="wizard-step-situation-content">
              {/* Letter-type-specific guidance (Feb 22, 2026) */}
              {letterType === 'ides_appeal_soldier' && (
                <div className="p-3 bg-rose-500/10 border-2 border-rose-500/40 rounded-lg space-y-2" data-testid="ides-appeal-guidance">
                  <div className="flex items-center gap-2">
                    <Shield className="w-4 h-4 text-rose-300 shrink-0" />
                    <p className="text-sm font-bold text-rose-200">IDES Appeal — write in your own voice</p>
                  </div>
                  <p className="text-[12px] text-rose-100/90 leading-relaxed">
                    This letter is <strong>yours</strong> — written in 1st person, signed by you. In the notes below, paste:
                  </p>
                  <ul className="text-[11px] text-rose-100/80 list-disc list-inside space-y-0.5 pl-2">
                    <li><strong>Board findings to dispute</strong> — copy from your IPEB/FPEB packet</li>
                    <li><strong>Your case # and notification date</strong> (top of board letter)</li>
                    <li><strong>Why each finding is wrong</strong> — wrong rating %, missed dx, severity downplayed, evidence ignored</li>
                    <li><strong>Supporting evidence you have</strong> — C&P exam dates, MRI, prior profiles, specialist letters</li>
                    <li><strong>Functional impact</strong> on duties + daily life (concrete examples)</li>
                  </ul>
                  <p className="text-[11px] text-amber-300/90 font-semibold pt-1">
                    ⏱ Most rebuttal windows are 5-10 days from notification. Don't wait.
                  </p>
                </div>
              )}
              {letterType === 'buddy_statement' && (
                <div className="p-3 bg-violet-500/10 border border-violet-500/40 rounded-lg space-y-2" data-testid="buddy-statement-guidance">
                  <div className="flex items-center gap-2">
                    <User className="w-4 h-4 text-violet-300 shrink-0" />
                    <p className="text-sm font-bold text-violet-200">Character / Buddy Statement</p>
                  </div>
                  <p className="text-[12px] text-violet-100/90 leading-relaxed">
                    <strong>You</strong> are the witness writing this. The soldier is the subject. In the notes below, describe what you've personally observed:
                  </p>
                  <ul className="text-[11px] text-violet-100/80 list-disc list-inside space-y-0.5 pl-2">
                    <li><strong>How you know the soldier</strong> — role, unit, how long</li>
                    <li><strong>Impact on work duties</strong> — what tasks they struggle with</li>
                    <li><strong>Time missed for medical appointments</strong> — frequency, pattern</li>
                    <li><strong>Pushing through in suboptimal health</strong> — specific shifts/days you saw it</li>
                    <li><strong>Visible symptoms</strong> you've personally witnessed</li>
                  </ul>
                  <p className="text-[11px] text-violet-200/80 pt-1">
                    Tip: use concrete examples (specific dates, specific tasks). Generalizations are weaker than firsthand details.
                  </p>
                </div>
              )}

              {!['ides_appeal_soldier', 'buddy_statement'].includes(letterType) && (
                <div className="p-3 bg-primary/5 border border-primary/10 rounded-lg">
                  <p className="text-sm text-muted-foreground">
                    {language === 'es'
                      ? 'Puede escribir en espa\u00f1ol. Su informaci\u00f3n ser\u00e1 procesada y la carta se generar\u00e1 en ingl\u00e9s (para el expediente) y en espa\u00f1ol (para usted).'
                      : 'Speak plainly \u2014 describe what\'s wrong, how long it\'s been going on, and how it affects your work or PT. WriteRight will translate this into proper medical language.'}
                  </p>
                </div>
              )}
              {language === 'es' && (
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-500/10 border border-amber-500/20 rounded-lg" data-testid="spanish-input-notice">
                  <Globe className="w-4 h-4 text-amber-400 shrink-0" />
                  <p className="text-xs text-amber-300">
                    Modo biling{'\u00fc'}e activo {'\u2014'} Your letter will be generated in <strong>English</strong> (provider copy) and <strong>Espa{'\u00f1'}ol</strong> (patient copy)
                  </p>
                </div>
              )}
              {/* Quick Tool: NSI Symptom Tracker — surfaced here so TBI patients don't have to dig through settings */}
              {!['ides_appeal_soldier', 'buddy_statement'].includes(letterType) && (
                <NSISymptomTracker variant="inline" testIdPrefix="wizard-nsi" />
              )}
              {NotesComponent && <NotesComponent {...notesProps} />}
              {RecordingComponent && <RecordingComponent {...recordingProps} />}
              {LimitationsComponent && <LimitationsComponent {...limitationsProps} />}
            </div>
          )}

          {/* Step 3: Your Information */}
          {currentStep === 2 && (
            <div className="space-y-4 wizard-step-enter" data-testid="wizard-step-info-content">
              <div className="p-3 bg-primary/5 border border-primary/10 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  {letterType === 'buddy_statement'
                    ? 'Enter YOUR info (the witness). The soldier should be referenced in your notes above. You sign this letter — not the soldier.'
                    : letterType === 'ides_appeal_soldier'
                      ? 'Your info goes here — you are signing this rebuttal. Use your full name, rank, MOS, and unit.'
                      : 'This information auto-fills into your letter. Your provider will verify everything.'}
                </p>
              </div>
              {DemographicsComponent && <DemographicsComponent {...demographicsProps} />}
              {/* Language Preference */}
              <div className="mt-4 p-3 border border-border/50 rounded-lg bg-muted/20" data-testid="language-selector">
                <div className="flex items-center gap-2 mb-2">
                  <Globe className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium text-foreground">Language / Idioma</span>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Choose your preferred language. If Spanish is selected, you can type in Spanish and your letter will be generated in both English and Spanish.
                </p>
                <div className="flex gap-2">
                  <Button
                    variant={language === 'en' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setLanguage && setLanguage('en')}
                    className="flex-1 h-9 text-sm gap-1.5"
                    data-testid="lang-en-btn"
                  >
                    English
                  </Button>
                  <Button
                    variant={language === 'es' ? 'default' : 'outline'}
                    size="sm"
                    onClick={() => setLanguage && setLanguage('es')}
                    className="flex-1 h-9 text-sm gap-1.5"
                    data-testid="lang-es-btn"
                  >
                    Espa{'\u00f1'}ol
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Step 4: Review & Generate */}
          {currentStep === 3 && (
            <div className="space-y-4 wizard-step-enter" data-testid="wizard-step-review-content">
              <div className="p-3 bg-primary/5 border border-primary/10 rounded-lg">
                <p className="text-sm text-muted-foreground">
                  Review your information below. Click <strong>Create Docs</strong> to generate your draft.
                  You can always go back to edit any step.
                </p>
              </div>
              {/* Summary */}
              <div className="space-y-3">
                <ReviewItem label="Document Mode" value={
                  documentMode === 'message' ? 'Portal Message' :
                  documentMode === 'both' ? 'Letter + Portal Message' : 'Letter'
                } onEdit={() => goToStep(0)} />
                {(documentMode === 'letter' || documentMode === 'both') && (
                  <ReviewItem label="Letter Type" value={LETTER_TYPES.find(lt => lt.value === letterType)?.label || letterType || 'Not selected'} onEdit={() => goToStep(0)} />
                )}
                {(documentMode === 'message' || documentMode === 'both') && messageScenario && (
                  <ReviewItem label="Message Scenario" value={MESSAGE_SCENARIOS.find(s => s.value === messageScenario)?.label || messageScenario.replace(/_/g, ' ')} onEdit={() => goToStep(0)} />
                )}
                <ReviewItem label="Situation" value={notes?.substring(0, 120) + (notes?.length > 120 ? '...' : '') || 'Not described'} onEdit={() => goToStep(1)} />
                <ReviewItem label="Name" value={[soldierFirstName, soldierLastName].filter(Boolean).join(' ') || 'Not provided'} onEdit={() => goToStep(2)} />
                {demographicsProps?.soldierDob && (
                  <ReviewItem label="DOB" value={demographicsProps.soldierDob} onEdit={() => goToStep(2)} />
                )}
                {demographicsProps?.soldierRank && (
                  <ReviewItem label="Rank" value={demographicsProps.soldierRank} onEdit={() => goToStep(2)} />
                )}
                {demographicsProps?.soldierUnit && (
                  <ReviewItem label="Unit" value={demographicsProps.soldierUnit} onEdit={() => goToStep(2)} />
                )}
                <ReviewItem label="Status" value={PATIENT_STATUSES.find(s => s.value === patientStatus)?.label || patientStatus || 'Not specified'} onEdit={() => goToStep(2)} />
                <ReviewItem label="Language" value={language === 'es' ? 'Espa\u00f1ol (Bilingual output)' : 'English'} onEdit={() => goToStep(2)} />
              </div>
              {/* Generate button */}
              <div className="pt-2">
                {ActionButtonsComponent && <ActionButtonsComponent {...actionButtonsProps} />}
              </div>
            </div>
          )}

          {/* Navigation — v6.8.49 gem-styled buttons matching the
              copper-trimmed dossier aesthetic. */}
          <div
            className="flex items-center justify-between mt-6 pt-4"
            style={{ borderTop: '1px solid rgba(218,165,32,0.18)' }}
          >
            <button
              type="button"
              onClick={prevStep}
              disabled={currentStep === 0}
              data-testid="wizard-back-btn"
              className={`flex items-center gap-2 px-4 py-2.5 border transition-all duration-200
                          ${currentStep === 0
                            ? 'opacity-30 cursor-not-allowed bg-secondary/20 text-muted-foreground border-border/30'
                            : 'bg-gradient-to-br from-slate-500 via-slate-700 to-slate-900 text-slate-50 border-slate-400/40 hover:-translate-y-0.5'}`}
              style={{
                borderRadius: 0,
                boxShadow: currentStep === 0 ? 'none' : [
                  'inset 0 2px 0 rgba(255,255,255,0.4)',
                  'inset 0 -2px 0 rgba(0,0,0,0.4)',
                  '0 3px 6px -1px rgba(0,0,0,0.5)',
                ].join(', '),
              }}
            >
              <ChevronLeft className="w-4 h-4" />
              <span
                className="text-[10px] tracking-widest"
                style={{
                  fontFamily: '"Cinzel", serif',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.18em',
                }}
              >Back</span>
            </button>
            <span
              className="text-[10px] italic"
              style={{
                fontFamily: '"Libre Baskerville", serif',
                color: 'rgba(218,165,32,0.75)',
              }}
            >
              {currentStep + 1} of {STEPS.length}
            </span>
            {currentStep < STEPS.length - 1 ? (
              <button
                type="button"
                onClick={nextStep}
                disabled={!canProceed()}
                data-testid="wizard-next-btn"
                className={`flex items-center gap-2 px-4 py-2.5 border transition-all duration-200
                            ${!canProceed()
                              ? 'opacity-30 cursor-not-allowed bg-secondary/20 text-muted-foreground border-border/30'
                              : 'bg-gradient-to-br from-amber-500 via-amber-700 to-amber-900 text-amber-50 border-amber-300/50 hover:-translate-y-0.5'}`}
                style={{
                  borderRadius: 0,
                  boxShadow: !canProceed() ? 'none' : [
                    'inset 0 2px 0 rgba(255,255,255,0.45)',
                    'inset 0 -2px 0 rgba(0,0,0,0.45)',
                    '0 3px 6px -1px rgba(0,0,0,0.5)',
                  ].join(', '),
                }}
              >
                <span
                  className="text-[10px] tracking-widest"
                  style={{
                    fontFamily: '"Cinzel", serif',
                    fontWeight: 700,
                    textTransform: 'uppercase',
                    letterSpacing: '0.18em',
                  }}
                >Next</span>
                <ChevronRight className="w-4 h-4" />
              </button>
            ) : (
              <div /> /* Empty spacer on review step */
            )}
          </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

/* Small summary row for review step */
const ReviewItem = ({ label, value, onEdit }) => (
  <div className="flex items-start justify-between gap-3 p-2.5 bg-muted/30 rounded-lg" data-testid={`review-${label.toLowerCase()}`}>
    <div className="min-w-0 flex-1">
      <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">{label}</span>
      <p className="text-sm text-foreground mt-0.5 break-words">{value}</p>
    </div>
    <Button variant="ghost" size="sm" onClick={onEdit} className="h-7 px-2 text-xs text-primary shrink-0" data-testid={`review-edit-${label.toLowerCase()}`}>
      Edit
    </Button>
  </div>
);

export default PatientWizard;
